import { createClient } from '@supabase/supabase-js'
import type { Database } from './types/database'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error(
    'Missing Supabase environment variables. Please check VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in your .env.local file.'
  )
}

// Validate URL format
try {
  new URL(supabaseUrl)
} catch {
  throw new Error(
    'Invalid VITE_SUPABASE_URL format. Expected format: https://your-project-id.supabase.co'
  )
}

// Create Supabase client with enhanced configuration for CraftyJoy
export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey, {
  auth: {
    // Configure authentication settings
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    
    // Child safety settings
    flowType: 'pkce', // More secure for public clients
    
    // Custom storage for authentication tokens
    storage: {
      getItem: (key: string) => {
        try {
          return localStorage.getItem(key)
        } catch {
          return null
        }
      },
      setItem: (key: string, value: string) => {
        try {
          localStorage.setItem(key, value)
        } catch {
          // Handle quota exceeded or other storage errors
          console.warn('Unable to save to localStorage:', key)
        }
      },
      removeItem: (key: string) => {
        try {
          localStorage.removeItem(key)
        } catch {
          console.warn('Unable to remove from localStorage:', key)
        }
      },
    },
  },
  
  // Database configuration
  db: {
    schema: 'public',
  },
  
  // Global configuration
  global: {
    headers: {
      'X-Client-Info': 'craftyjoy-web@1.0.0',
    },
  },
})

// Helper function to check if Supabase is properly configured
export const checkSupabaseConfig = (): { isValid: boolean; errors: string[] } => {
  const errors: string[] = []
  
  if (!supabaseUrl || supabaseUrl === 'https://your-prod-project-id.supabase.co') {
    errors.push('VITE_SUPABASE_URL is not configured with a real project URL')
  }
  
  if (!supabaseAnonKey || supabaseAnonKey === 'your-Development-anon-key') {
    errors.push('VITE_SUPABASE_ANON_KEY is not configured with a real anonymous key')
  }
  
  // Check if URL matches expected Supabase format
  if (supabaseUrl && !supabaseUrl.match(/^https:\/\/[a-z]{20}\.supabase\.co$/)) {
    errors.push('VITE_SUPABASE_URL does not match expected Supabase format')
  }
  
  return {
    isValid: errors.length === 0,
    errors,
  }
}

// Helper function to get current session
export const getCurrentSession = async () => {
  try {
    const { data: { session }, error } = await supabase.auth.getSession()
    
    if (error) {
      console.error('Error getting session:', error)
      return null
    }
    
    return session
  } catch (error) {
    console.error('Error getting current session:', error)
    return null
  }
}

// Helper function to get current user
export const getCurrentUser = async () => {
  try {
    const { data: { user }, error } = await supabase.auth.getUser()
    
    if (error) {
      console.error('Error getting user:', error)
      return null
    }
    
    return user
  } catch (error) {
    console.error('Error getting current user:', error)
    return null
  }
}

// Helper function for development/testing
export const isDemoMode = () => {
  // Check runtime demo mode flag (set in production when Supabase is not configured)
  if ((window as any).__CRAFTYJOY_DEMO_MODE__) {
    return true
  }
  
  return import.meta.env.VITE_APP_ENVIRONMENT === 'dev' && (
    !supabaseUrl || 
    supabaseUrl === 'https://your-prod-project-id.supabase.co' ||
    !supabaseAnonKey || 
    supabaseAnonKey === 'your-Development-anon-key'
  )
}